import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ComponentBase } from '~/infrastructure/component-base';
import { ISiteListing } from '~/models/modules/Common/siteDto';
import {
    SiteTransferRequestCreateModel,
    SiteTransferRequestDetail,
    SiteTransferRequestSummary,
    SiteTransferRequestUpdateModel
} from '~/models/modules/Stocks/site-transfer';
import { MultiSiteService } from '~/services/modules/common/multi-site.service';
import { InventoriesService } from '~/services/modules/stocks/inventories';
import { SiteTransfersService } from '~/services/modules/stocks/site-transfers';
import { SitesEnum } from '~/shared/enums/sites.enum';
import { SharedService } from '~/services/sharedService';
import { GridLayoutManagerService } from '~/shared/components/grid/layout/grid-layout-manager';
import { LayoutManagerService } from '~/shared/components/layout/layout-manager';
import { ThemeManagerService } from '~/services/theme-manager';
import { LayoutComponent } from '~/shared/components/layout/layout.component';
import { ErrorManagerService } from '~/services/error-manager';
import { PageState } from '~/shared/enums/stock-files.enum';
import { PageNames } from '~/models/pageNames';

interface EditableLineItem {
    id?: number;
    stockLocationId: number;
    itemNumber: string;
    itemDescription: string;
    locationCode: string;
    availableQuantity: number;
    quantity: number;
    transferredQuantity?: number;
}

@Component({
    selector: 'app-stocks-site-transfer-upsert',
    templateUrl: './upsert.html',
    styleUrls: ['./upsert.scss'],
    standalone: false
})
export class StocksSiteTransferUpsertComponent extends ComponentBase implements OnInit, OnDestroy {
    @Input() requestId: number | null = null;
    @Output() closed = new EventEmitter<boolean>();
    @ViewChild('mainWindow') kendoMainWindow: any;

    _form: FormGroup;
    _active: boolean = false;
    _isNew: boolean = true;
    _isEdit: boolean = false;
    _isLocked: boolean = false;
    _loading: boolean = false;
    _saving: boolean = false;
    _errorMessage: string = '';
    _theme: string = 'enterprise';
    _pageState: any;
    _windowState: any = 'default';
    data: any;

    isGeneralInformationExpanded: boolean = true;
    isAddItemExpanded: boolean = true;

    _allSites: ISiteListing[] = [];
    _availableFromSites: ISiteListing[] = [];
    _filteredFromSites: ISiteListing[] = [];
    _availableToSites: ISiteListing[] = [];
    _filteredToSites: ISiteListing[] = [];
    _currentUserSiteId: number = SitesEnum.AllSites;
    _isSiteFromLocked: boolean = false;

    _availableStockLocations: any[] = [];
    _filteredStockLocations: any[] = [];
    _selectedStockLocation: any = null;
    _inputQuantity: number = 1;

    _lineItems: EditableLineItem[] = [];

    get _statusLabel(): string {
        if (this._isLocked) return 'Dispatched';
        if (this._isEdit) return 'Pending';
        return 'New';
    }

    constructor(
        private _fb: FormBuilder,
        private _siteTransfersService: SiteTransfersService,
        private _multiSiteService: MultiSiteService,
        private _inventoriesService: InventoriesService,
        private _sharedService: SharedService,
        private _gridLayoutManagerService: GridLayoutManagerService,
        private _layoutManager: LayoutManagerService,
        private _themeManager: ThemeManagerService,
        private _layoutComponent: LayoutComponent,
        private _errorManagerService: ErrorManagerService
    ) {
        super();
        this._initForm();

        this.data = this._sharedService?.model;
        if (this.data) {
            this._pageState = this.data.pageState;
            this._isNew = this.data.isNew || this._pageState === PageState.ADD;
            const model = this.data.model;
            if (model && model.id) {
                this.requestId = model.id;
            } else if (typeof model === 'number') {
                this.requestId = model;
            }
            this._active = true;
        } else {
            this._active = true;
        }

        if (this._themeManager) {
            this.registerSubscription(
                this._themeManager.themeChange.subscribe((data) => {
                    this._theme = data;
                })
            );
        }

        if (this._layoutManager) {
            this._layoutManager.applyOverly();
        }
    }

    ngOnInit(): void {
        this._isEdit = !!this.requestId;
        if (this._isEdit && !this._pageState) {
            this._pageState = PageState.EDIT;
        } else if (!this._pageState) {
            this._pageState = PageState.ADD;
        }
        this._detectUserSite();
        this._loadSites();
    }

    ngOnDestroy(): void {
        if (this._layoutManager) {
            this._layoutManager.removeOverlay();
        }
    }

    _onStateChanges(e: any): void {
        const element: any = this.kendoMainWindow;
        if (e === 'minimized') {
            element?.el?.nativeElement?.classList?.remove('fullWidthFormForm');
            this._layoutManager?.removeOverlay();
        } else if (e === 'maximized') {
            element?.el?.nativeElement?.classList?.remove('fullWidthFormForm');
            this._layoutManager?.applyOverly();
        } else if (e === 'default') {
            element?.el?.nativeElement?.classList?.add('fullWidthFormForm');
            this._layoutManager?.applyOverly();
        }
    }

    ExpandCollapseGeneralInfoWidget(): void {
        this.isGeneralInformationExpanded = !this.isGeneralInformationExpanded;
    }

    ExpandCollapseAddItemWidget(): void {
        this.isAddItemExpanded = !this.isAddItemExpanded;
    }

    private _initForm(): void {
        this._form = this._fb.group({
            siteFromId: [null, Validators.required],
            siteToId: [null, Validators.required],
            notes: ['']
        });
    }

    private _detectUserSite(): void {
        const loggedInAccountSites = localStorage.getItem('loggedInAccountSites');
        if (loggedInAccountSites) {
            try {
                const site = JSON.parse(loggedInAccountSites)[0];
                if (site && site.siteId) {
                    this._currentUserSiteId = site.siteId;
                }
            } catch (e) {
                this._currentUserSiteId = SitesEnum.AllSites;
            }
        }
    }

    private _loadSites(): void {
        this._multiSiteService.getAllSites().subscribe({
            next: (sites: ISiteListing[]) => {
                this._allSites = sites.filter(s => s.id !== SitesEnum.AllSites);
                this._availableFromSites = [...this._allSites];
                this._filteredFromSites = [...this._availableFromSites];

                if (this._currentUserSiteId !== SitesEnum.AllSites) {
                    this._isSiteFromLocked = false;
                    if (!this._form.value.siteFromId) {
                        this._form.patchValue({ siteFromId: this._currentUserSiteId });
                        this._filterToSites(this._currentUserSiteId);
                        this._loadAvailableStockLocations(this._currentUserSiteId);
                    }
                }

                if (this._isEdit && this.requestId) {
                    this._loadExistingRequest(this.requestId);
                }
            },
            error: (err) => console.error('Error loading sites:', err)
        });
    }

    private _filterToSites(selectedFromId: number): void {
        this._availableToSites = this._allSites.filter(s => s.id !== +selectedFromId);
        this._filteredToSites = [...this._availableToSites];
        if (this._form.value.siteToId === +selectedFromId) {
            this._form.patchValue({ siteToId: null });
        }
    }

    _handleFromSitesFilter(filter: string): void {
        if (!filter) {
            this._filteredFromSites = [...this._availableFromSites];
        } else {
            const query = filter.toLowerCase();
            this._filteredFromSites = this._availableFromSites.filter(s =>
                (s.name && s.name.toLowerCase().includes(query)) ||
                (s.code && s.code.toLowerCase().includes(query))
            );
        }
    }

    _handleToSitesFilter(filter: string): void {
        if (!filter) {
            this._filteredToSites = [...this._availableToSites];
        } else {
            const query = filter.toLowerCase();
            this._filteredToSites = this._availableToSites.filter(s =>
                (s.name && s.name.toLowerCase().includes(query)) ||
                (s.code && s.code.toLowerCase().includes(query))
            );
        }
    }

    _handleStockLocationsFilter(filter: string): void {
        if (!filter) {
            this._filteredStockLocations = [...this._availableStockLocations];
        } else {
            const query = filter.toLowerCase();
            this._filteredStockLocations = this._availableStockLocations.filter(x =>
                (x.displayLabel && x.displayLabel.toLowerCase().includes(query)) ||
                (x.itemNumber && x.itemNumber.toLowerCase().includes(query)) ||
                (x.itemDescription && x.itemDescription.toLowerCase().includes(query)) ||
                (x.locationCode && x.locationCode.toLowerCase().includes(query))
            );
        }
    }

    _onSiteFromChange(siteFromId: any): void {
        if (siteFromId) {
            this._filterToSites(+siteFromId);
            this._lineItems = []; // Clear line items on site change
            this._loadAvailableStockLocations(+siteFromId);
        } else {
            this._availableStockLocations = [];
            this._filteredStockLocations = [];
            this._selectedStockLocation = null;
        }
    }

    private _loadAvailableStockLocations(siteId: number): void {
        if (!siteId) {
            this._availableStockLocations = [];
            this._filteredStockLocations = [];
            this._selectedStockLocation = null;
            return;
        }

        const model = {
            filter: {},
            HideZeroQuantities: true,
            SiteId: +siteId
        };
        const params = {
            page: 1,
            pageSize: 500,
            siteId: +siteId
        };

        this._inventoriesService.getInventories(params, model, +siteId).subscribe({
            next: (res: any) => {
                const rawList = res?.data || (Array.isArray(res) ? res : []);
                this._availableStockLocations = rawList
                    .map((x: any) => {
                        const stockLocId = x.stockLocationId || x.id;
                        const itemNumber = x.itemsItemCode || x.stockItem?.number || x.stockItem?.itemCode || x.stockNumber || x.itemNumber || x.inventoryNumber || '';
                        const itemDescription = x.stockItemsDescriptionTextual || x.stockItem?.description || x.description || x.itemDescription || '';
                        const locationCode = x.locationsLocationCode || x.location?.code || x.locationCode || '';
                        const avail = x.itemsQuantity != null ? x.itemsQuantity : (x.availability != null ? x.availability : (x.quantity != null ? x.quantity : (x.availableQuantity != null ? x.availableQuantity : 0)));

                        return {
                            ...x,
                            id: stockLocId,
                            stockLocationId: stockLocId,
                            itemNumber: itemNumber,
                            itemDescription: itemDescription,
                            locationCode: locationCode,
                            availableQuantity: Number(avail),
                            displayLabel: `${itemNumber}${itemDescription ? ' - ' + itemDescription : ''} (Loc: ${locationCode || 'N/A'}, Avail: ${avail})`
                        };
                    })
                    .filter((x: any) => x.availableQuantity > 0 && !!x.itemNumber);

                this._filteredStockLocations = [...this._availableStockLocations];
            },
            error: (err) => {
                console.error('Error loading stock locations:', err);
                this._availableStockLocations = [];
                this._filteredStockLocations = [];
            }
        });
    }

    private _loadExistingRequest(id: number): void {
        this._loading = true;
        this._siteTransfersService.getSiteTransfer(id).subscribe({
            next: (data: SiteTransferRequestSummary) => {
                this._loading = false;
                this._form.patchValue({
                    siteFromId: data.siteFromId,
                    siteToId: data.siteToId,
                    notes: data.notes || ''
                });

                this._filterToSites(data.siteFromId);

                // Lockout check: if dispatched, disable editing
                if (data.transferredOut || (data.details && data.details.some(d => d.transferredQuantity > 0))) {
                    this._isLocked = true;
                    this._form.disable();
                }

                this._lineItems = (data.details || []).map(d => ({
                    id: d.id,
                    stockLocationId: d.stockLocationId,
                    itemNumber: d.itemNumber,
                    itemDescription: d.itemDescription,
                    locationCode: d.locationCode,
                    availableQuantity: d.availableQuantity,
                    quantity: d.quantity,
                    transferredQuantity: d.transferredQuantity
                }));

                this._loadAvailableStockLocations(data.siteFromId);
            },
            error: (err) => {
                this._loading = false;
                const msg = this._extractErrorMessage(err, 'Failed to load transfer request details.');
                this._showNotification(msg, 'Error');
                console.error(err);
            }
        });
    }

    _addLineItem(): void {
        if (!this._selectedStockLocation) {
            this._showNotification('Please select a stock inventory item.', 'Validation');
            return;
        }

        if (!this._inputQuantity || this._inputQuantity <= 0) {
            this._showNotification('Quantity must be greater than zero.', 'Validation');
            return;
        }

        const avail = this._selectedStockLocation.availableQuantity ?? (this._selectedStockLocation.availability || this._selectedStockLocation.quantity || 0);
        if (this._inputQuantity > avail) {
            this._showNotification(`Quantity cannot exceed available stock (${avail}).`, 'Validation');
            return;
        }

        const itemNum = this._selectedStockLocation.itemNumber || this._selectedStockLocation.stockItem?.number || this._selectedStockLocation.stockNumber || '';
        const itemDesc = this._selectedStockLocation.itemDescription || this._selectedStockLocation.stockItem?.description || this._selectedStockLocation.description || '';
        const locCode = this._selectedStockLocation.locationCode || this._selectedStockLocation.location?.code || '';

        const existing = this._lineItems.find(x => x.stockLocationId === this._selectedStockLocation.id);
        if (existing) {
            existing.quantity = this._inputQuantity;
            this._lineItems = [...this._lineItems];
        } else {
            this._lineItems = [
                ...this._lineItems,
                {
                    stockLocationId: this._selectedStockLocation.id,
                    itemNumber: itemNum,
                    itemDescription: itemDesc,
                    locationCode: locCode,
                    availableQuantity: avail,
                    quantity: this._inputQuantity
                }
            ];
        }

        this._selectedStockLocation = null;
        this._inputQuantity = 1;
        this._errorMessage = '';
    }

    _removeLineItem(index: number): void {
        if (!this._isLocked) {
            this._lineItems.splice(index, 1);
            this._lineItems = [...this._lineItems];
        }
    }

    _removeHandler(args: any): void {
        if (args && args.rowIndex !== undefined) {
            this._removeLineItem(args.rowIndex);
        }
    }

    _onItemQuantityChange(val: number, item: EditableLineItem): void {
        if (val !== null && val !== undefined) {
            if (val > item.availableQuantity) {
                item.quantity = item.availableQuantity;
            } else if (val < 1) {
                item.quantity = 1;
            } else {
                item.quantity = val;
            }
        }
    }

    _onSubmit(action: 'save' | 'saveAndClose' = 'saveAndClose'): void {
        this._errorMessage = '';

        if (this._form.invalid) {
            this._showNotification('Please complete all required fields.', 'Validation');
            return;
        }

        if (this._lineItems.length === 0) {
            this._showNotification('Please add at least one line item to the transfer.', 'Validation');
            return;
        }

        const overStockItem = this._lineItems.find(l => l.quantity > l.availableQuantity);
        if (overStockItem) {
            this._showNotification(`Transfer quantity for item "${overStockItem.itemNumber}" exceeds available stock (${overStockItem.availableQuantity}).`, 'Validation');
            return;
        }

        const invalidQtyItem = this._lineItems.find(l => !l.quantity || l.quantity <= 0);
        if (invalidQtyItem) {
            this._showNotification(`Transfer quantity for item "${invalidQtyItem.itemNumber}" must be greater than zero.`, 'Validation');
            return;
        }

        this._saving = true;
        const formVal = this._form.getRawValue();

        if (this._isEdit && this.requestId) {
            const updateModel: SiteTransferRequestUpdateModel = {
                id: this.requestId,
                siteFromId: +formVal.siteFromId,
                siteToId: +formVal.siteToId,
                notes: formVal.notes,
                items: this._lineItems.map(l => ({
                    id: l.id,
                    stockLocationId: l.stockLocationId,
                    quantity: l.quantity
                }))
            };

            this._siteTransfersService.updateSiteTransfer(this.requestId, updateModel).subscribe({
                next: () => {
                    this._saving = false;
                    this._gridLayoutManagerService.setGridDataSubjects(PageNames.SiteTransfers);
                    if (action === 'saveAndClose') {
                        this._closeForm(true);
                    } else {
                        if (this.requestId) {
                            this._loadExistingRequest(this.requestId);
                        }
                    }
                },
                error: (err) => {
                    this._saving = false;
                    const msg = this._extractErrorMessage(err, 'Failed to update transfer request.');
                    this._showNotification(msg, 'Error');
                }
            });
        } else {
            const createModel: SiteTransferRequestCreateModel = {
                siteFromId: +formVal.siteFromId,
                siteToId: +formVal.siteToId,
                notes: formVal.notes,
                items: this._lineItems.map(l => ({
                    stockLocationId: l.stockLocationId,
                    quantity: l.quantity
                }))
            };

            this._siteTransfersService.createSiteTransfer(createModel).subscribe({
                next: (res: any) => {
                    this._saving = false;
                    this._gridLayoutManagerService.setGridDataSubjects(PageNames.SiteTransfers);
                    if (action === 'saveAndClose') {
                        this._closeForm(true);
                    } else {
                        const newId = res?.id || res?.data?.id || (typeof res === 'number' ? res : null);
                        if (newId) {
                            this.requestId = newId;
                            this._isEdit = true;
                            this._isNew = false;
                            this._pageState = PageState.EDIT;
                            this._loadExistingRequest(newId);
                        } else {
                            this._closeForm(true);
                        }
                    }
                },
                error: (err) => {
                    this._saving = false;
                    const msg = this._extractErrorMessage(err, 'Failed to create transfer request.');
                    this._showNotification(msg, 'Error');
                }
            });
        }
    }

    private _showNotification(message: string, title: string = 'Alert'): void {
        if (this._layoutComponent) {
            this._layoutComponent.openPopup({
                title: title,
                message: message,
                withoutConfirm: true
            });
        } else if (this._errorManagerService) {
            this._errorManagerService.setError({
                title: title,
                message: message,
                withoutConfirm: true
            });
        }
    }

    private _extractErrorMessage(err: any, fallback: string): string {
        if (!err) {
            return fallback;
        }
        if (typeof err === 'string') {
            return err;
        }
        if (typeof err.error === 'string') {
            return err.error;
        }
        if (err.error && typeof err.error.message === 'string') {
            return err.error.message;
        }
        if (err.error && typeof err.error.error === 'string') {
            return err.error.error;
        }
        if (err.error && typeof err.error.data === 'string') {
            return err.error.data;
        }
        if (err.error && typeof err.error.data?.message === 'string') {
            return err.error.data.message;
        }
        if (err.error && typeof err.error.title === 'string') {
            let msg = err.error.title;
            if (err.error.errors && typeof err.error.errors === 'object') {
                const details: string[] = [];
                for (const key of Object.keys(err.error.errors)) {
                    const val = err.error.errors[key];
                    if (Array.isArray(val)) {
                        details.push(...val);
                    } else if (typeof val === 'string') {
                        details.push(val);
                    }
                }
                if (details.length > 0) {
                    msg = details.join('<br/>');
                }
            }
            return msg;
        }
        if (err.error && err.error.errors && typeof err.error.errors === 'object') {
            const details: string[] = [];
            for (const key of Object.keys(err.error.errors)) {
                const val = err.error.errors[key];
                if (Array.isArray(val)) {
                    details.push(...val);
                } else if (typeof val === 'string') {
                    details.push(val);
                }
            }
            if (details.length > 0) {
                return details.join('<br/>');
            }
        }
        if (typeof err.message === 'string') {
            return err.message;
        }
        return fallback;
    }

    _closeForm(refresh: boolean = false): void {
        const closeComponent = {
            component: PageNames.SiteTransfers,
            isClosed: true,
            response: refresh ? true : null
        };
        this._active = false;
        if (refresh) {
            this._gridLayoutManagerService.setGridDataSubjects(PageNames.SiteTransfers);
        }
        if (this._layoutManager) {
            this._layoutManager.removeOverlay();
        }
        if (this._sharedService) {
            this._sharedService.closeDialog.emit(closeComponent);
            this._sharedService.model = {};
        }
        this.closed.emit(refresh);
    }

    _onCancel(): void {
        this._closeForm(false);
    }
}
